import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { placeService } from '../services/api';

// Tipos para as cafeterias
export interface Cafeteria {
  id: string;
  name: string;
  city: string;
  state: string;
  status: string;
  role: string;
  logoUrl?: string;
  isActive: boolean;
}

export interface CafeteriaDetails {
  id: string;
  name: string;
  description?: string;
  address: string;
  city?: string;
  state?: string;
  instagram?: string;
  logoUrl?: string;
  coverImageUrl?: string;
  cityId?: string;
  status: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CafeteriaStats {
  placeId: string;
  placeName: string;
  totalProducts: number;
  totalReviews: number;
  averageRating: number;
  totalFavorites: number;
  lastUpdated: string;
}

interface CafeteriaSelectionContextType {
  // Estado
  accessibleCafeterias: Cafeteria[];
  currentCafeteria: Cafeteria | null;
  currentCafeteriaDetails: CafeteriaDetails | null;
  isLoading: boolean;
  error: string | null;
  
  // Ações
  loadAccessibleCafeterias: () => Promise<void>;
  selectCafeteria: (cafeteriaId: string) => Promise<boolean>;
  validateCafeteriaAccess: (cafeteriaId: string) => Promise<boolean>;
  getCafeteriaDetails: (cafeteriaId: string) => Promise<CafeteriaDetails | null>;
  getCafeteriaStats: (cafeteriaId: string) => Promise<CafeteriaStats | null>;
  
  // Utilitários
  hasAccessToCafeteria: (cafeteriaId: string) => boolean;
  getUserRoleInCafeteria: (cafeteriaId: string) => string;
}

const CafeteriaSelectionContext = createContext<CafeteriaSelectionContextType | undefined>(undefined);

interface CafeteriaSelectionProviderProps {
  children: ReactNode;
}

export const CafeteriaSelectionProvider: React.FC<CafeteriaSelectionProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  // Estado
  const [accessibleCafeterias, setAccessibleCafeterias] = useState<Cafeteria[]>([]);
  const [currentCafeteria, setCurrentCafeteria] = useState<Cafeteria | null>(null);
  const [currentCafeteriaDetails, setCurrentCafeteriaDetails] = useState<CafeteriaDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carregar cafeterias acessíveis
  const loadAccessibleCafeterias = async () => {
    if (!isAuthenticated) {
      setError('Usuário não autenticado');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await placeService.getAccessiblePlaces();
      const cafeterias: Cafeteria[] = data.places.map((place: any) => ({
        id: place.id,
        name: place.name,
        city: place.city,
        state: place.state,
        status: place.status,
        role: place.role,
        logoUrl: place.logoUrl,
        isActive: place.isActive,
      }));

      console.info('Cafeterias acessíveis carregadas:', cafeterias);
      setAccessibleCafeterias(cafeterias);

      // Remover a seleção automática daqui - será feita pelo useEffect
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      console.error('Erro ao carregar cafeterias:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Selecionar uma cafeteria
  const selectCafeteria = async (cafeteriaId: string): Promise<boolean> => {
    if (!isAuthenticated) {
      setError('Usuário não autenticado');
      return false;
    }

    // Validar acesso primeiro
    const hasAccess = await validateCafeteriaAccess(cafeteriaId);
    if (!hasAccess) {
      setError('Você não tem acesso a esta cafeteria');
      return false;
    }

    try {
      // Buscar detalhes da cafeteria
      const details = await getCafeteriaDetails(cafeteriaId);
      if (!details) {
        setError('Não foi possível obter detalhes da cafeteria');
        return false;
      }

      // Encontrar a cafeteria na lista de acessíveis
      console.info(accessibleCafeterias, cafeteriaId);
      const cafeteria = accessibleCafeterias.find(c => c.id === cafeteriaId);
      if (!cafeteria) {
        setError('Cafeteria não encontrada na lista de acessíveis');
        return false;
      }

      // Atualizar estado
      setCurrentCafeteria(cafeteria);
      setCurrentCafeteriaDetails(details);
      setError(null);

      // Salvar no localStorage
      localStorage.setItem('selectedCafeteriaId', cafeteriaId);
      localStorage.setItem('selectedCafeteriaData', JSON.stringify(cafeteria));

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      console.error('Erro ao selecionar cafeteria:', err);
      return false;
    }
  };

  // Validar acesso a uma cafeteria
  const validateCafeteriaAccess = async (cafeteriaId: string): Promise<boolean> => {
    if (!isAuthenticated) {
      return false;
    }

    try {
      const data = await placeService.validatePlaceAccess(cafeteriaId);
      return data.hasAccess === true;
    } catch (err) {
      console.error('Erro ao validar acesso:', err);
      return false;
    }
  };

  // Obter detalhes de uma cafeteria
  const getCafeteriaDetails = async (cafeteriaId: string): Promise<CafeteriaDetails | null> => {
    if (!isAuthenticated) {
      return null;
    }

    try {
      const data = await placeService.getPlaceDetails(cafeteriaId);
      return {
        id: data.id,
        name: data.name,
        description: data.description,
        address: data.address,
        city: data.city,
        state: data.state,
        instagram: data.instagram,
        logoUrl: data.logoUrl,
        coverImageUrl: data.coverImageUrl,
        cityId: data.cityId,
        status: data.status,
        role: data.role,
        isActive: data.isActive,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      };
    } catch (err) {
      console.error('Erro ao obter detalhes da cafeteria:', err);
      return null;
    }
  };

  // Obter estatísticas de uma cafeteria
  const getCafeteriaStats = async (cafeteriaId: string): Promise<CafeteriaStats | null> => {
    if (!isAuthenticated) {
      return null;
    }

    try {
      const data = await placeService.getPlaceStats(cafeteriaId);
      return {
        placeId: data.placeId,
        placeName: data.placeName,
        totalProducts: data.totalProducts,
        totalReviews: data.totalReviews,
        averageRating: data.averageRating,
        totalFavorites: data.totalFavorites,
        lastUpdated: data.lastUpdated,
      };
    } catch (err) {
      console.error('Erro ao obter estatísticas da cafeteria:', err);
      return null;
    }
  };

  // Verificar se o usuário tem acesso a uma cafeteria
  const hasAccessToCafeteria = (cafeteriaId: string): boolean => {
    return accessibleCafeterias.some(c => c.id === cafeteriaId);
  };

  // Obter o papel do usuário em uma cafeteria
  const getUserRoleInCafeteria = (cafeteriaId: string): string => {
    const cafeteria = accessibleCafeterias.find(c => c.id === cafeteriaId);
    return cafeteria?.role || 'NoAccess';
  };

  // Carregar cafeteria salva no localStorage ao inicializar
  useEffect(() => {
    if (isAuthenticated && accessibleCafeterias.length > 0) {
      const savedCafeteriaId = localStorage.getItem('selectedCafeteriaId');
      if (savedCafeteriaId && hasAccessToCafeteria(savedCafeteriaId)) {
        selectCafeteria(savedCafeteriaId);
      } else if (accessibleCafeterias.length > 0) {
        // Se não há cafeteria salva ou não tem acesso, selecionar a primeira ativa
        const firstActive = accessibleCafeterias.find(c => c.isActive) || accessibleCafeterias[0];
        selectCafeteria(firstActive.id);
      }
    }
  }, [isAuthenticated, accessibleCafeterias]);

  // Carregar cafeterias quando o usuário se autenticar
  useEffect(() => {
    if (isAuthenticated) {
      loadAccessibleCafeterias();
    }
  }, [isAuthenticated]);

  const value: CafeteriaSelectionContextType = {
    // Estado
    accessibleCafeterias,
    currentCafeteria,
    currentCafeteriaDetails,
    isLoading,
    error,
    
    // Ações
    loadAccessibleCafeterias,
    selectCafeteria,
    validateCafeteriaAccess,
    getCafeteriaDetails,
    getCafeteriaStats,
    
    // Utilitários
    hasAccessToCafeteria,
    getUserRoleInCafeteria,
  };

  return (
    <CafeteriaSelectionContext.Provider value={value}>
      {children}
    </CafeteriaSelectionContext.Provider>
  );
};

export const useCafeteriaSelection = (): CafeteriaSelectionContextType => {
  const context = useContext(CafeteriaSelectionContext);
  if (context === undefined) {
    throw new Error('useCafeteriaSelection deve ser usado dentro de um CafeteriaSelectionProvider');
  }
  return context;
};
